import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Footer } from 'ui/components/4-habitats/Footer';
import { useRouter } from 'next/router';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { LocaleContext } from 'application/adapters/context/LocaleContext';
import { currentCustomer } from 'application/adapters/store/slices/customerSlice';
import { useFormContext } from 'application/adapters/context/FormContext';
import { useContactLinkQuery } from 'application/hooks/useContactLinkQuery';

interface FooterFeatureProps {
	culture: Umbraco.Cultures;
	pageFooter: Umbraco.FooterCard[];
	contactLink: Umbraco.Link;
	languages?: Umbraco.LanguageSelector;
	locations: Umbraco.Locations;
	socialLinks: Umbraco.SocialLink[];
}

export const FooterFeature: React.FC<FooterFeatureProps> = ({ culture, pageFooter, contactLink, locations, languages, socialLinks }) => {
	const router = useRouter();
	const dictionary = useContext(DictionaryContext);
	const formContext = useFormContext();
	const { offices } = locations?.locations?.pages[0]?.properties ?? {};
	const footerLanguageSelectorProps = languages?.languageSelector?.pages[0]?.properties ?? {};
	const footerDictionary = {
		footerAddressHeading: dictionary.getValue('Footer.AddressHeading', null, 'Company'),
	};
	const localeContext = useContext(LocaleContext);
	const localeCountry = localeContext.getLocaleCountry();
	const localeLanguage = localeContext.getLocaleLanguage();
	const businessPartner = useSelector(currentCustomer);

	const handleLanguageChange = (language: string, country: string) => {
		localeContext.setLocaleParams(language, country);
		setSelectedLocale(localeContext.formatLanguage(language, country));
	};

	// Cookie preference is applied after hydration to avoid SSR mismatch, but will update immediately on client.
	const [selectedLocale, setSelectedLocale] = useState({
		language: localeLanguage,
		country: localeCountry || null,
	});

	// After navigation, restore country from cookie if present and matching language.
	// Ensures user's explicit country choice persists across reloads and locale changes.
	useEffect(() => {
		let effectiveCountry = localeCountry;
		if (!effectiveCountry) {
			const cookieLocale = localeContext.getLocaleFromCookie();
			if (cookieLocale?.country && cookieLocale?.language === localeLanguage) {
				effectiveCountry = cookieLocale.country;
			}
		}
		handleLanguageChange(localeLanguage, effectiveCountry);
		setSelectedLocale(localeContext.formatLanguage(localeLanguage, effectiveCountry));
	}, [localeLanguage, localeCountry]);

	const isChineseCulture = culture?.toLowerCase().startsWith('zh');

	// Extract query param and buildUrlWithParams helper from hook
	const { buildUrlWithParams } = useContactLinkQuery({ contactLink });

	const handleContactForm = () => {
		// Build URL with contact param while preserving existing UTM params
		const queryString = buildUrlWithParams(true);
		const newUrl = `${window.location.pathname}${queryString ? '?' + queryString : ''}${window.location.hash}`;
		window.history.pushState(null, '', newUrl);
		formContext.open();
	};

	// Turn this off until Specs for which logged in user should have auto-selected address are more CLEAR.
	// Update country to SoldTo address country
	// useEffect(() => {
	// 	if (businessPartner?.addresses) {
	// 		// Check if the business partner has addresses
	// 		const soldToCountry = businessPartner?.addresses?.find((address) => address.key === 'soldTo')?.value?.country?.trim();

	// 		const bPCountry = soldToCountry || 'DK';

	// 		if (localeCountry !== bPCountry) {
	// 			localeContext.setLocaleCountry(bPCountry);
	// 			setSelectedLocale(localeContext.formatLanguage(localeLanguage, bPCountry));
	// 		}
	// 	}
	// }, [businessPartner?.addresses]);

	return (
		<Footer
			footerCards={pageFooter}
			offices={offices}
			contactLink={contactLink}
			languageSelectorProps={{
				...footerLanguageSelectorProps,
				dropdownLabel: footerLanguageSelectorProps?.dropdownLabel || 'Select language',
			}}
			selectedLocale={selectedLocale?.country ? `${selectedLocale?.language} / ${selectedLocale?.country}` : selectedLocale?.language}
			socialLinksItems={socialLinks}
			culture={router.locale}
			dictionary={footerDictionary}
			handleCountrySelection={handleLanguageChange}
			handleContactForm={handleContactForm}
			contactFormLoading={formContext.isLoading}
			chineseCulture={isChineseCulture}
		/>
	);
};
